import { Trans, useLingui } from "@lingui/react/macro";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
  MoreHorizontalIcon,
} from "lucide-react";
import * as React from "react";

import { buttonVariants, type Button } from "@/components/ui-kit/base/button";
import { cn } from "@/utils/shadcn-ui";

function Pagination({ className, ...props }: React.ComponentProps<"nav">) {
  const { t } = useLingui();
  return (
    <nav
      role="navigation"
      aria-label={t`pagination`}
      data-slot="pagination"
      className={cn("mx-auto flex w-full justify-center", className)}
      {...props}
    />
  );
}

function PaginationContent({
  className,
  ...props
}: React.ComponentProps<"ul">) {
  return (
    <ul
      data-slot="pagination-content"
      className={cn("flex flex-row items-center gap-1", className)}
      {...props}
    />
  );
}

function PaginationItem({ ...props }: React.ComponentProps<"li">) {
  return <li data-slot="pagination-item" {...props} />;
}

type PaginationLinkProps = {
  isActive?: boolean;
} & Pick<React.ComponentProps<typeof Button>, "size" | "iconOnly"> &
  React.ComponentProps<"a">;

function PaginationLink({
  className,
  isActive,
  size = "md",
  iconOnly = true,
  ...props
}: PaginationLinkProps) {
  return (
    <a
      aria-current={isActive ? "page" : undefined}
      data-slot="pagination-link"
      data-active={isActive}
      className={cn(
        buttonVariants({
          variant: isActive ? "secondary" : "tertiary",
          size,
          iconOnly,
        }),
        "cursor-pointer select-none",
        className,
      )}
      {...props}
    />
  );
}

function PaginationPrevious({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) {
  const { t } = useLingui();
  return (
    <PaginationLink
      aria-label={t`Go to previous page`}
      iconOnly={false}
      className={cn("gap-1 px-2.5 sm:pl-2.5", className)}
      {...props}
    >
      <ChevronLeftIcon />
      <span className="hidden sm:block">
        <Trans>Previous</Trans>
      </span>
    </PaginationLink>
  );
}

function PaginationNext({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) {
  const { t } = useLingui();
  return (
    <PaginationLink
      aria-label={t`Go to next page`}
      iconOnly={false}
      className={cn("gap-1 px-2.5 sm:pr-2.5", className)}
      {...props}
    >
      <span className="hidden sm:block">
        <Trans>Next</Trans>
      </span>
      <ChevronRightIcon />
    </PaginationLink>
  );
}

function PaginationFirst({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) {
  const { t } = useLingui();
  return (
    <PaginationLink
      aria-label={t`Go to first page`}
      className={cn(className)}
      {...props}
    >
      <ChevronsLeftIcon />
    </PaginationLink>
  );
}

function PaginationLast({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) {
  const { t } = useLingui();
  return (
    <PaginationLink
      aria-label={t`Go to last page`}
      className={cn(className)}
      {...props}
    >
      <ChevronsRightIcon />
    </PaginationLink>
  );
}

function PaginationEllipsis({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      aria-hidden
      data-slot="pagination-ellipsis"
      className={cn("flex size-9 items-center justify-center", className)}
      {...props}
    >
      <MoreHorizontalIcon className="size-4" />
      <span className="sr-only">
        <Trans>More pages</Trans>
      </span>
    </span>
  );
}

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationFirst,
  PaginationItem,
  PaginationLast,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
};
